import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [state, setState] = useState("loading"); // loading | valid | invalid | success
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Strip the token from the address bar so it isn't kept in history/referrers.
    if (token) {
      window.history.replaceState({}, document.title, "/reset-password");
    }
    if (!token) {
      setState("invalid");
      setError("This reset link is missing its token.");
      return;
    }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setState("valid"))
      .catch((e) => { setError(formatApiError(e)); setState("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setState("success");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div className="font-heading font-extrabold text-2xl text-slate-900">FixTrack</div>
        </div>

        {state === "loading" && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500" data-testid="reset-loading">
            Checking your reset link…
          </CardContent></Card>
        )}

        {state === "invalid" && (
          <Card className="border-slate-200" data-testid="reset-invalid-card">
            <CardHeader>
              <CardTitle className="font-heading">Link invalid or expired</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">{error || "This reset link is invalid, expired, or already used."}</p>
              <Link to="/forgot-password">
                <Button variant="outline" data-testid="reset-request-new">Request a new link</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state === "valid" && (
          <Card className="border-slate-200" data-testid="reset-form-card">
            <CardHeader>
              <CardTitle className="font-heading">Choose a new password</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Enter and confirm your new password below.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rp-password">New password *</Label>
                  <Input
                    id="rp-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    data-testid="reset-password"
                  />
                  <p className="text-xs text-slate-500">Minimum 8 characters.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rp-confirm">Confirm password *</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    data-testid="reset-confirm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-700 hover:bg-blue-800"
                  data-testid="reset-submit"
                >
                  {submitting ? "Resetting…" : "Reset password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {state === "success" && (
          <Card className="border-emerald-200" data-testid="reset-success-card">
            <CardContent className="p-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">Password reset!</div>
              <p className="text-sm text-slate-600">Redirecting you to sign in…</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
